import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, User, Shield, SignOut } from "@phosphor-icons/react";

const NavLink = ({ to, label, active }) => (
  <Link
    to={to}
    data-testid={`nav-${label.toLowerCase()}`}
    className={`label-mono !text-sm !font-bold transition-colors duration-200 hover:text-primary ${active ? "text-primary" : "text-muted-foreground"}`}
  >
    {label}
  </Link>
);

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const { pathname } = useLocation();

  const goCoaVault = () => {
    const scroll = () => document.getElementById("coa-vault")?.scrollIntoView({ behavior: "smooth" });
    if (pathname === "/") { scroll(); }
    else { nav("/"); setTimeout(scroll, 500); }
  };

  return (
    <>
      <div className="w-full bg-[#1A1508] border-b border-primary/30 relative z-[3]">
        <p className="text-center label-mono text-primary py-2 text-[0.65rem]">
          ⚠ For Laboratory & Research Use Only
        </p>
      </div>
      <header className="sticky top-0 z-50 bg-[#0A1836]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="logo-home" className="flex items-center gap-3 group">
            <img src="/logo_mark.png" alt="Mulan Peptide Collection" className="h-11 w-11 object-contain group-hover:scale-105 transition-transform" style={{ transition: "transform 0.2s" }} />
            <div className="leading-none">
              <span className="font-head font-semibold text-lg tracking-[0.15em] text-primary">MULAN</span>
              <span className="block label-mono text-[0.55rem] text-muted-foreground mt-0.5">Peptide Collection</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <NavLink to="/" label="Home" active={pathname === "/"} />
            <NavLink to="/p/about" label="About" active={pathname === "/p/about"} />
            <NavLink to="/shop" label="Catalog" active={pathname === "/shop"} />
            <button
              data-testid="nav-coa-vault"
              onClick={goCoaVault}
              className="label-mono !text-sm !font-bold text-muted-foreground hover:text-primary transition-colors duration-200"
            >
              COA Vault
            </button>
            <NavLink to="/p/contact" label="Contact" active={pathname === "/p/contact"} />
            {user?.role === "admin" && <NavLink to="/admin" label="Admin" active={pathname === "/admin"} />}
          </nav>

          <div className="flex items-center gap-5">
            {user ? (
              <>
                <Link to="/account" data-testid="nav-account" className="text-muted-foreground hover:text-primary transition-colors" title="Account">
                  <User size={20} />
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin-icon" className="md:hidden text-muted-foreground hover:text-primary transition-colors">
                    <Shield size={20} />
                  </Link>
                )}
                <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="text-muted-foreground hover:text-destructive transition-colors" title="Log out">
                  <SignOut size={20} />
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login" className="label-mono !text-sm !font-bold text-muted-foreground hover:text-primary transition-colors">
                Sign In
              </Link>
            )}
            <Link to="/cart" data-testid="nav-cart" className="relative text-foreground hover:text-primary transition-colors">
              <ShoppingBag size={22} />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-primary text-black text-[0.6rem] font-mono font-semibold h-4 min-w-4 px-1 grid place-items-center rounded-full">
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>
    </>
  );
}
